import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Star, Check, PoundSterling } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import ReviewSlider from "@/components/ReviewSlider";
import GuestyWidget from "@/components/GuestyWidget";
import { useContent } from "@/hooks/useContent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const { content: c } = useContent();
  const [property, setProperty] = useState(null);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const scrollToWidget = () => {
    const widget = document.getElementById('search-widget_IO312PWQ');
    if (widget) {
      widget.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const fetchData = async () => {
    try {
      const [propRes, reviewRes] = await Promise.all([
        axios.get(`${API}/properties`),
        axios.get(`${API}/reviews?featured=true`)
      ]);
      
      if (propRes.data.length > 0) {
        setProperty(propRes.data[0]);
      }
      setReviews(reviewRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Video Hero Section */}
      <section className="video-hero" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/30 to-primary/60" />
        <img 
          src="https://images.unsplash.com/photo-1753187991725-76940ce624c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          alt="Luxury apartment interior"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="video-hero-overlay">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center z-10 px-4 max-w-6xl w-full"
          >
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-4 sm:mb-6 font-heading" data-testid="hero-title">
              {c.hero_title || "Your Perfect Stay Awaits"}
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-white/90 mb-8 sm:mb-12 max-w-2xl mx-auto">
              {c.hero_subtitle || "Premium serviced apartments for unforgettable experiences"}
            </p>
            
            {/* Guesty Booking Widget */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="w-full max-w-4xl mx-auto px-4 sm:px-0"
            >
              <div className="glass rounded-2xl p-5 sm:p-8 shadow-2xl">
                <GuestyWidget />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Trust Badge */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="bg-secondary py-4"
      >
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-center gap-3">
          <Check className="w-6 h-6 text-primary" />
          <span className="text-lg font-medium text-primary" data-testid="price-match-badge">
            {c.trust_badge || "Book Direct & Save 12% \u2022 Guaranteed Best Price"}
          </span>
        </div>
      </motion.div>

      {/* About Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-5xl font-semibold text-primary mb-6 font-heading">
                {c.about_heading || "More than a bed\u2014a space to live"}
              </h2>
              <p className="text-base md:text-lg text-slate-600 mb-6 leading-relaxed">
                {c.about_paragraph_1 || "At KSpace Properties, we provide premium serviced apartments that blend comfort with style."}
              </p>
              <p className="text-base md:text-lg text-slate-600 mb-8 leading-relaxed">
                {c.about_paragraph_2 || "Featuring high-quality interiors, modern amenities, and 24/7 support, we ensure every stay is seamless."}
              </p>
              <Button 
                size="lg" 
                className="bg-primary hover:bg-primary/90 text-white rounded-full"
                onClick={scrollToWidget}
                data-testid="view-property-button"
              >
                {c.about_button || "View Our Property"}
              </Button>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-2 gap-4"
            >
              <img 
                src="https://images.unsplash.com/photo-1710886250891-18ba7c38c2be?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="Living room"
                className="rounded-lg shadow-lg property-image w-full h-64 object-cover"
              />
              <img 
                src="https://images.unsplash.com/photo-1771287491132-4954b32210d6?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="Bedroom"
                className="rounded-lg shadow-lg property-image w-full h-64 object-cover mt-8"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-20 md:py-32 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-semibold text-primary mb-4 font-heading">
              {c.location_heading || "Perfectly Located in Brighton"}
            </h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              {c.location_subtitle || "Steps away from The Lanes and minutes from the beach"}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="md:col-span-5 space-y-6"
            >
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-start gap-4">
                  <MapPin className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-medium text-primary mb-2">{c.location_1_name || "The Lanes"}</h3>
                    <p className="text-slate-600">{c.location_1_desc || "2 minutes walk \u2022 Brighton\u2019s iconic shopping quarter"}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-start gap-4">
                  <MapPin className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-medium text-primary mb-2">{c.location_2_name || "Brighton Beach"}</h3>
                    <p className="text-slate-600">{c.location_2_desc || "4 minutes walk \u2022 Seafront & Palace Pier"}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-start gap-4">
                  <MapPin className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-medium text-primary mb-2">{c.location_3_name || "Royal Pavilion"}</h3>
                    <p className="text-slate-600">{c.location_3_desc || "5 minutes walk \u2022 Historic landmark"}</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="md:col-span-7"
            >
              <div className="rounded-lg overflow-hidden shadow-lg h-full min-h-[400px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2521.5!2d-0.13742!3d50.82012!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTDCsDQ5JzEyLjQiTiAwwrAwOCcxNC43Ilc!5e0!3m2!1sen!2suk!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: '400px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Property Location"
                  data-testid="location-map"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      {reviews.length > 0 && (
        <section className="py-20 md:py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-5xl font-semibold text-primary mb-4 font-heading">
                {c.reviews_heading || "What Our Guests Say"}
              </h2>
              <p className="text-base md:text-lg text-slate-600">
                {c.reviews_subtitle || "Real reviews from our Airbnb guests"}
              </p>
            </motion.div>
            <ReviewSlider reviews={reviews} />
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-primary text-white">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center px-4"
        >
          <h2 className="text-3xl md:text-5xl font-semibold mb-6 font-heading">
            {c.cta_heading || "Ready to Book Your Stay?"}
          </h2>
          <p className="text-lg md:text-xl mb-8 text-white/90">
            {c.cta_subtitle || "Experience Brighton\u2019s finest serviced apartment. Book direct and save."}
          </p>
          <Button 
            size="lg" 
            className="bg-accent hover:bg-accent/90 text-white text-lg px-8 py-6 rounded-full"
            onClick={scrollToWidget}
            data-testid="cta-book-button"
          >
            <PoundSterling className="w-5 h-5 mr-2" />
            {c.cta_button || "Check Availability"}
          </Button>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="bg-secondary py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h3 className="text-2xl font-heading font-semibold text-primary mb-4">{c.footer_company || "KSpace Properties"}</h3>
          <p className="text-slate-600 mb-4">{c.footer_email || "info@kspaceproperties.com"}</p>
          <a 
            href={c.footer_instagram_url || "https://www.instagram.com/kspaceproperties"} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-primary hover:text-accent transition-colors inline-block mb-6"
          >
            {c.footer_instagram_text || "Follow us on Instagram"}
          </a>
          <div className="border-t border-border pt-6 mt-6">
            <p className="text-slate-500 text-sm">
              &copy; {new Date().getFullYear()} {c.footer_company || "KSpace Properties"}. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating Mobile Button */}
      <Button
        className="floating-book-btn bg-accent hover:bg-accent/90 text-white rounded-full px-6 py-6"
        onClick={scrollToWidget}
        data-testid="floating-book-button"
      >
        <PoundSterling className="w-5 h-5 mr-2" />
        Book Now
      </Button>
    </div>
  );
};

export default Home;
